import { Label } from '@askable/ui/core/label';
import { Spinner } from '@askable/ui/core/spinner';
import { cn } from '@askable/ui/lib/utils';
import { Pencil } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type EditableHeaderTitleProps = {
  icon?: React.ElementType;
  isSaving?: boolean;
  placeholder?: string;
  title: string;
  onTitleChange: (newTitle: string) => void;
};

export const EditableHeaderTitle = ({
  icon: Icon,
  isSaving = false,
  placeholder,
  title,
  onTitleChange,
}: EditableHeaderTitleProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [hasInitiatedSave, setHasInitiatedSave] = useState(false);

  // Update local title when external title changes (but not while editing)
  useEffect(() => {
    if (!isEditing) {
      setLocalTitle(title);
    }
  }, [title, isEditing]);

  // When saving is complete, exit editing mode
  useEffect(() => {
    if (hasInitiatedSave && !isSaving) {
      setIsEditing(false);
      setHasInitiatedSave(false);
    }
  }, [hasInitiatedSave, isSaving]);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault?.();
      const trimmedTitle = localTitle.trim();

      if (trimmedTitle !== title) {
        // If the trimmed title is empty, use the original title, placeholder, or translated fallback
        const titleToSave =
          trimmedTitle === '' ? title || placeholder || t('sections.studies.untitledStudy') : trimmedTitle;
        onTitleChange(titleToSave);
        setHasInitiatedSave(true);
      } else {
        setIsEditing(false);
      }
    },
    [localTitle, title, placeholder, onTitleChange, t],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLocalTitle(title);
        setIsEditing(false);
      }
    },
    [title],
  );

  const handleBlur = useCallback(() => {
    const trimmedTitle = localTitle.trim();
    if (trimmedTitle !== title) {
      // If the trimmed title is empty, use the original title, placeholder, or translated fallback
      const titleToSave =
        trimmedTitle === '' ? title || placeholder || t('sections.studies.untitledStudy') : trimmedTitle;
      onTitleChange(titleToSave);
      setHasInitiatedSave(true);
    } else {
      setIsEditing(false);
    }
  }, [localTitle, title, placeholder, onTitleChange, t]);

  const displayTitle = localTitle || placeholder || title;
  const isTitleEmpty = localTitle === '';

  if (isEditing) {
    return (
      <form className="-mt-[1px] flex items-center gap-1" onSubmit={handleSubmit}>
        <Label htmlFor="title" className="sr-only">
          {title}
        </Label>
        <input
          type="text"
          autoComplete="off"
          autoFocus={!isSaving}
          disabled={isSaving}
          className={cn(
            `h-8 w-full flex-1 rounded-md border-0 p-2 text-sm font-medium leading-normal text-foreground focus-visible:outline-none`,
            { 'cursor-not-allowed opacity-50': isSaving },
          )}
          data-1p-ignore
          id="title"
          name="title"
          placeholder={placeholder || title}
          value={localTitle}
          onChange={e => setLocalTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
        {isSaving ? <Spinner className="size-4" /> : null}
      </form>
    );
  }

  return (
    <div className="-mt-[1px] flex h-8 flex-1 items-center gap-1 text-sm font-medium text-foreground">
      {Icon ? <Icon className="size-4" /> : null}
      <button
        className={cn('group flex h-8 w-full items-center justify-start gap-1 p-2 text-sm font-medium', {
          'pointer-events-none cursor-not-allowed': isSaving,
        })}
        onClick={() => !isSaving && setIsEditing(true)}
      >
        <div
          className={cn('truncate leading-normal', {
            'text-muted-foreground': isTitleEmpty,
          })}
        >
          {displayTitle}
        </div>
        <Pencil className="size-4 opacity-0 transition-opacity duration-150 group-focus-within:opacity-100 group-hover:opacity-100 group-focus:opacity-100" />
      </button>
    </div>
  );
};
